<template>
  <div class="weekly-view-manager w-full max-w-full min-w-0">
    <div class="weekly-view-manager__filters">
      <span class="inline-flex items-center gap-1 mr-1 text-xs font-bold uppercase text-gray-500">
        <b-icon pack="fas" icon="eye" size="is-small" />
        {{ $t('groups') }}
      </span>
      <button
        v-for="group in groupFilters"
        :key="group.key"
        type="button"
        class="activities-weekly-table__group-chip inline-flex items-center gap-1.5 py-0.5 px-2 border-0 rounded-full text-white text-xs font-bold cursor-pointer"
        :class="{ 'activities-weekly-table__group-chip--hidden': isGroupHidden(group.columnGroup) }"
        :style="{ backgroundColor: group.color }"
        :aria-pressed="isGroupHidden(group.columnGroup)"
        @click="toggleColumnGroup(group.columnGroup)"
      >
        {{ $t(group.labelKey) }}
        <span class="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-white text-gray-800 text-xs font-bold">{{ group.count }}</span>
      </button>
    </div>

    <div v-if="isLoading" class="py-8 text-center text-sm text-gray-500">
      {{ $t('loading') }}...
    </div>

    <div v-else-if="!managers.length" class="py-8 text-center text-sm text-gray-500">
      {{ $t('no_results') }}
    </div>

    <div v-else class="weekly-view-manager__list">
      <div
        v-for="(manager, index) in managers"
        :key="manager.id"
        class="weekly-view-manager__card"
      >
        <div class="weekly-view-manager__card-inner">
          <span
            class="weekly-view-manager__accent"
            :style="{ backgroundColor: managerColor(index) }"
          />

          <div class="weekly-view-manager__card-body">
            <div
              class="weekly-view-manager__row"
              @click="toggleManager(manager)"
            >
              <button
                type="button"
                class="weekly-view-manager__expand-btn"
                :aria-expanded="isExpanded(manager)"
                @click.stop="toggleManager(manager)"
              >
                <b-icon
                  pack="fas"
                  :icon="isExpanded(manager) ? 'chevron-down' : 'chevron-right'"
                  size="is-small"
                />
              </button>

              <span class="weekly-view-manager__type-badge">
                {{ $t('manager') }}
              </span>

              <span class="weekly-view-manager__avatar-wrap">
                <img
                  v-if="manager.avatar"
                  :src="manager.avatar"
                  :alt="manager.fullname || manager.name"
                  class="weekly-view-manager__avatar"
                >
                <span
                  v-else
                  class="weekly-view-manager__avatar weekly-view-manager__avatar--placeholder"
                  :style="{ backgroundColor: managerColor(index) }"
                >
                  {{ managerInitials(manager) }}
                </span>
              </span>

              <span
                class="weekly-view-manager__name-badge"
                :style="{ backgroundColor: managerColor(index) }"
              >
                {{ manager.fullname || manager.name }}
              </span>

              <span class="weekly-view-manager__counts">
                {{ managerCountsLabel(manager) }}
              </span>

              <span
                v-if="managerProgressLabel(manager)"
                class="weekly-view-manager__progress"
                :style="{ color: managerColor(index) }"
              >
                {{ managerProgressLabel(manager) }}
              </span>
            </div>

            <div
              v-if="isExpanded(manager)"
              class="weekly-view-manager__panel"
            >
              <div
                v-if="!hasManagerActivities(manager)"
                class="weekly-view-manager__empty"
              >
                {{ $t('no_activities_for_manager') }}
              </div>
              <activities-weekly-table
                v-else
                :key="'manager-activities-' + manager.id"
                :project-id="projectId"
                :manager-user-id="manager.id"
                activities-load-mode="manager"
                embedded
                :week-start-date="weekStartDate"
                :week-end-date="weekEndDate"
                :week-number="weekNumber"
                :hidden-column-groups="hiddenColumnGroups"
                :filter-subactivities-by-week="filterSubactivitiesByWeek"
                :reschedule-mode="rescheduleMode"
                :edited-subactivities="editedSubactivities"
                :activity-filters="activityFilters"
                @update-rescheduled-activity="$emit('update-rescheduled-activity', $event)"
                @remove-rescheduled-activity="$emit('remove-rescheduled-activity', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ActivitiesWeeklyTable from './activities-weekly-table.vue'

const MANAGER_COLORS = ['#f97316', '#8b5cf6', '#3b82f6', '#22c55e', '#ec4899', '#14b8a6', '#6366f1']

export default {
  name: 'weekly-view-manager',

  components: {
    ActivitiesWeeklyTable,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    filterSubactivitiesByWeek: {
      type: Boolean,
      default: true,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    editedSubactivities: {
      type: Array,
      default: () => [],
    },
    activityFilters: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isLoading: false,
      managers: [],
      expandedManagerIds: [],
      hiddenColumnGroups: [],
      groupFilters: [
        { key: 'info', columnGroup: 'tracking', labelKey: 'information', color: '#3b82f6', count: 13 },
        { key: 'planned_advance', columnGroup: 'planned_advance', labelKey: 'planned_advance', color: '#1e3a5f', count: 6 },
        { key: 'scheduled_advance', columnGroup: 'scheduled_advance', labelKey: 'scheduled_advance', color: '#166534', count: 6 },
        { key: 'real', columnGroup: 'real', labelKey: 'real_progress', color: '#7c3aed', count: 5 },
      ],
    }
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadManagers()
      },
    },
    weekStartDate() {
      this.loadManagers()
    },
    weekEndDate() {
      this.loadManagers()
    },
    filterSubactivitiesByWeek() {
      this.loadManagers()
    },
  },

  methods: {
    // activityFilters solo aplican a activities-weekly-table, no a la lista de encargados.
    buildRequestParams() {
      const params = {}

      if (this.filterSubactivitiesByWeek && this.weekStartDate && this.weekEndDate) {
        params.weekly_start = this.weekStartDate
        params.weekly_end = this.weekEndDate
      }

      return params
    },

    async loadManagers() {
      if (!this.projectId) {
        this.managers = []
        return
      }

      this.isLoading = true

      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/activity-managers`, {
          params: this.buildRequestParams(),
        })

        this.managers = data && data.managers ? data.managers : []
      } catch (error) {
        this.managers = []
        this.$notify.error('error_loading_activities')
      } finally {
        this.isLoading = false
      }
    },

    managerColor(index) {
      return MANAGER_COLORS[index % MANAGER_COLORS.length]
    },

    managerInitials(manager) {
      const label = manager.fullname || manager.name || ''
      const parts = label.trim().split(/\s+/).filter(Boolean)

      if (!parts.length) {
        return '?'
      }

      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
      }

      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },

    managerCountsLabel(manager) {
      return this.$t('activities_subactivities_count', {
        activities: Number(manager.activities_count || 0),
        subactivities: Number(manager.sub_activities_count || 0),
      })
    },

    managerProgressLabel(manager) {
      const subCount = Number(manager.sub_activities_count || 0)
      const progress = manager.sub_activities_avg_progress

      if (subCount === 0 || progress == null) {
        return null
      }

      return this.$t('average_progress', {
        value: Math.round(Number(progress)),
      })
    },

    hasManagerActivities(manager) {
      return Number(manager.sub_activities_count || 0) > 0
        || Number(manager.activities_count || 0) > 0
    },

    isExpanded(manager) {
      return this.expandedManagerIds.includes(manager.id)
    },

    toggleManager(manager) {
      if (this.isExpanded(manager)) {
        this.expandedManagerIds = this.expandedManagerIds.filter((id) => id !== manager.id)
        return
      }

      this.expandedManagerIds = [...this.expandedManagerIds, manager.id]
    },

    isGroupHidden(groupKey) {
      return this.hiddenColumnGroups.includes(groupKey)
    },

    toggleColumnGroup(groupKey) {
      if (this.isGroupHidden(groupKey)) {
        this.hiddenColumnGroups = this.hiddenColumnGroups.filter((key) => key !== groupKey)
        return
      }

      this.hiddenColumnGroups = [...this.hiddenColumnGroups, groupKey]
    },
  },
}
</script>

<style scoped>
.activities-weekly-table__group-chip--hidden {
  opacity: 0.45;
}

.weekly-view-manager__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid #c5d4eb;
  border-radius: 0.5rem;
  background-color: #fff;
}

.weekly-view-manager__list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.weekly-view-manager__card {
  min-width: 0;
  border: 1px solid #dbe6f5;
  border-radius: 0.5rem;
  background-color: #fff;
  overflow: hidden;
}

.weekly-view-manager__card-inner {
  display: flex;
  min-width: 0;
}

.weekly-view-manager__accent {
  width: 4px;
  flex-shrink: 0;
  align-self: stretch;
}

.weekly-view-manager__card-body {
  flex: 1;
  min-width: 0;
}

.weekly-view-manager__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}

.weekly-view-manager__expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6b7280;
}

.weekly-view-manager__type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: #4b5563;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.weekly-view-manager__avatar-wrap {
  flex-shrink: 0;
}

.weekly-view-manager__avatar {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  object-fit: cover;
}

.weekly-view-manager__avatar--placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 700;
}

.weekly-view-manager__name-badge {
  display: inline-flex;
  align-items: center;
  max-width: 14rem;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.weekly-view-manager__counts {
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.weekly-view-manager__progress {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.weekly-view-manager__panel {
  border-top: 1px solid #dbe6f5;
  background-color: #fff;
}

.weekly-view-manager__empty {
  padding: 1.5rem 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #6b7280;
}
</style>
